import { useState, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../../hooks/useGuardedNavigate";
import { Layout, Menu, Tour } from "antd";
import type { MenuProps } from "antd";
import {
  HomeOutlined,
  TeamOutlined,
  UserOutlined,
  GoldOutlined,
  ProfileOutlined,
  PieChartOutlined,
  TagOutlined,
  ShopOutlined,
  FileTextOutlined,
  SkinOutlined,
  SettingOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
} from "@ant-design/icons";

import { AuthContext } from "../../context/AuthContext";
import { TourContext } from "../../context/TourContext";

import {
  getGroupsApi,
} from "../../services/Group/GroupApis";

const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group"
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem;
}

const SideBar: React.FC = () => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const { refNavigation, markPageAsVisited, pages, setOpen, setPageLoaded } =
    useContext(TourContext);

  const navigate = useGuardedNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [funeralEnabled, setFuneralEnabled] = useState(true); // Start as true to avoid layout shift
  const [role, setRole] = useState("");
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const onClick: MenuProps["onClick"] = (e) => {
    void navigate(e.key);
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const menuItems = useMemo(() => {
    const isAdminOrAbove = role === "Admin" || role === "Super Admin";
    const isSuperAdmin = role === "Super Admin";
    const activeGroup = (currentUser?.Group ?? []).find(
      (g: any) => String(g.id) === String(currentGroup)
    );
    const isExternal = activeGroup?.IsExternal === true;
    const showPayments = isExternal;

    const defaultItems = [
      getItem("Dashboard", "/dashboard", <HomeOutlined />),
      ...(isAdminOrAbove
        ? [getItem("Compliance", "/compliance", <SafetyCertificateOutlined />)]
        : []),
      ...(isSuperAdmin
        ? [getItem("Maintenance", "/maintenance", <ToolOutlined />)]
        : []),
      ...(!isExternal ? [getItem("Members", "/members", <TeamOutlined />)] : []),
      getItem("Groups", "/groups", <GoldOutlined />),
      getItem("Users", "/users", <UserOutlined />),
      // Removed while we work out agent management
      // getItem("Agents", "/agents", <SkinOutlined/>),
      getItem("Reports", "/reports", <PieChartOutlined />),
      getItem("Claims", "/claims", <TagOutlined />),
      ...(showPayments ? [getItem("Payments", "/payments", <DollarOutlined />)] : []),
      ...(isAdminOrAbove ? [getItem("Config", "/report-config", <SettingOutlined />)] : []),
    ];

    if (role === "Data Capturer" || role === "Agent") {
      return [
        getItem("Dashboard", "/dashboard", <HomeOutlined />),
        ...(!isExternal ? [getItem("Members", "/members", <TeamOutlined />)] : []),
      ];
    } else if (role === "Finance") {
      return [
        getItem("Dashboard", "/dashboard", <HomeOutlined />),
        ...(!isExternal ? [getItem("Members", "/members", <TeamOutlined />)] : []),
        getItem("Reports", "/reports", <PieChartOutlined />),
        getItem("Claims", "/claims", <TagOutlined />),
      ];
    }

    if (funeralEnabled) {
      return [
        ...defaultItems,
        getItem("Funeral", "sub1", <ShopOutlined />, [
          getItem("Invoices", "/funeral-invoices", <FileTextOutlined />),
          getItem("Products", "/funeral-products", <ProfileOutlined />),
          getItem("Customers", "/funeral-customers", <UserOutlined />),
        ]),
      ];
    } else {
      return defaultItems;
    }
  }, [role, funeralEnabled, currentGroup, currentUser?.Group]);

  useEffect(() => {
    if (!currentUser || !currentUser.Role) return;
    
    setRole(currentUser.Role.Name);
    getGroupsApi()
      .then((res) => {
        const { data } = res;
        const isFuneralEnabled = data.some(
          (group: any) => group.FuneralEnabled === true
        );
        setFuneralEnabled(isFuneralEnabled);
      })
      .catch((err) => {
        console.log(err);
        setFuneralEnabled(false);
      });
  }, [currentGroup, currentUser]);

  useEffect(() => {
    // If the current location is within the Funeral submenu, open it
    if (funeralEnabled && location.pathname.startsWith("/funeral")) {
      setOpenKeys(["sub1"]);
    } else {
      setOpenKeys([]);
    }
  }, [funeralEnabled, location]);

  const siderWidth = window.innerWidth < 768 ? 200 : 200;
  const collapsedWidth = window.innerWidth < 768 ? 0 : 80;
  const spacerWidth = collapsed ? collapsedWidth : siderWidth;

  return (
    <>
      <div
        style={{
          width: spacerWidth,
          flexShrink: 0,
          transition: "width 0.2s",
        }}
      />
      <Sider
        ref={refNavigation}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        breakpoint="lg"
        collapsedWidth={collapsedWidth}
        width={siderWidth}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          height: "100vh",
          overflow: "auto",
          zIndex: 10,
        }}
      >
        <div className="space" />

        <Menu
          onClick={onClick}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          selectedKeys={[
            location.pathname.startsWith("/maintenance")
              ? "/maintenance"
              : location.pathname,
          ]}
          theme="light"
          mode="inline"
          items={menuItems}
        />
      </Sider>
    </>
  );
};

export default SideBar;
